import React, { useEffect, useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { COLORS } from './statisticsUtils';
import SalesOriginBreakdown from './SalesOriginBreakdown';
import { SALES_ORIGIN_TEXT } from '../../constants';
import './salesMetrics.css';

const MONTH_NAMES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const Y_AXIS_LABEL = 'Cantidad de ventas';
const NO_DATA_MESSAGE = 'No hay datos para mostrar';
const CHART_HEIGHT = 400;
const WEEKDAY_LETTERS_BY_JS_DAY = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];
const WEEKDAY_NAMES_BY_JS_DAY = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const CHART_MARGINS = { top: 20, right: 30, left: 20, bottom: 30 };
const X_AXIS_HEIGHT = 50;
const DAY_TICK_VALUE_DY = 16;
const WEEKDAY_TICK_VALUE_DY = 30;
const GRID_DASH_PATTERN = '3 3';
const LINE_THICKNESS_PX = 2;
const POINT_BORDER_THICKNESS_PX = 2;
const PREVIOUS_PERIOD_LINE_DASH_PATTERN = '6 4';
const CURRENT_PERIOD_POINT_RADIUS_PX = 4;
const CURRENT_PERIOD_HOVERED_POINT_RADIUS_PX = 6;
const PREVIOUS_PERIOD_POINT_RADIUS_PX = 3;
const PREVIOUS_PERIOD_HOVERED_POINT_RADIUS_PX = 5;
const CURRENT_PERIOD_POINT = {
  fill: COLORS.primaryStroke,
  strokeWidth: POINT_BORDER_THICKNESS_PX,
  r: CURRENT_PERIOD_POINT_RADIUS_PX,
};
const CURRENT_PERIOD_HOVERED_POINT = { r: CURRENT_PERIOD_HOVERED_POINT_RADIUS_PX };
const PREVIOUS_PERIOD_POINT = {
  fill: COLORS.secondary,
  strokeWidth: POINT_BORDER_THICKNESS_PX,
  r: PREVIOUS_PERIOD_POINT_RADIUS_PX,
};
const PREVIOUS_PERIOD_HOVERED_POINT = { r: PREVIOUS_PERIOD_HOVERED_POINT_RADIUS_PX };
const OPEN_BREAKDOWN_KEYS = ['Enter', ' '];

const preventFocusOnMouseDown = (mouseEvent) => {
  mouseEvent.preventDefault();
};

const getMonthLabel = (monthIndex) => {
  if (monthIndex > 0 && monthIndex <= MONTH_NAMES.length) {
    return MONTH_NAMES[monthIndex - 1];
  }
  return monthIndex;
};

const getJsWeekday = (dayNumber, targetYear, targetMonth) => {
  if (!dayNumber || !targetYear || !targetMonth) {
    return null;
  }
  const weekday = new Date(targetYear, targetMonth - 1, dayNumber).getDay();
  return Number.isNaN(weekday) ? null : weekday;
};

const getWeekdayName = (dayNumber, targetYear, targetMonth) => {
  const weekday = getJsWeekday(dayNumber, targetYear, targetMonth);
  if (weekday === null) {
    return '';
  }
  return WEEKDAY_NAMES_BY_JS_DAY[weekday];
};

const DailySalesTooltip = ({
  active,
  payload,
  label,
  isYearlyView,
  currentLabel,
  previousLabel,
  showPreviousLine,
  year,
  month,
  previousYear,
  previousMonth,
}) => {
  if (!active || !payload?.length) return null;
  const chartPoint = payload?.[0]?.payload;
  const displayLabel = isYearlyView ? getMonthLabel(label) : label;
  const currentWeekdayName = isYearlyView ? '' : getWeekdayName(label, year, month);
  const previousWeekdayName = isYearlyView ? '' : getWeekdayName(label, previousYear, previousMonth);
  return (
    <div className="salesChartTooltip">
      <p><strong>{isYearlyView ? 'Mes ' : 'Día '}{displayLabel}{currentWeekdayName ? ` (${currentWeekdayName})` : ''}</strong></p>
      <p className="salesCurrentPeriod">
        {currentLabel}: {chartPoint?.currentCount}
      </p>
      {showPreviousLine && (
        <p className="salesPreviousPeriod">
          {previousLabel}{previousWeekdayName ? ` (${previousWeekdayName})` : ''}: {chartPoint?.previousCount}
        </p>
      )}
      {!isYearlyView && <small className="text-muted">{SALES_ORIGIN_TEXT.TOOLTIP_HINT}</small>}
    </div>
  );
};

const DailySalesTimeSeriesChart = ({
  data,
  previousData = [],
  showPreviousLine = false,
  currentLabel = 'Mes actual',
  previousLabel = 'Mes anterior',
  isYearlyView = false,
  year = null,
  month = null,
  previousYear = null,
  previousMonth = null,
}) => {
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [activePoint, setActivePoint] = useState(null);

  const mergedData = useMemo(() => {
    const currentByKey = new Map();
    const previousByKey = new Map();

    (data || []).forEach((item) => {
      const keyValue = isYearlyView ? Number(item.month) : Number(item.day);
      currentByKey.set(keyValue, item);
    });

    (previousData || []).forEach((item) => {
      const keyValue = isYearlyView ? Number(item.month) : Number(item.day);
      previousByKey.set(keyValue, item);
    });

    const keySet = new Set([...currentByKey.keys(), ...previousByKey.keys()]);
    const sortedKeys = Array.from(keySet).filter(key => !Number.isNaN(key)).sort((keyA, keyB) => keyA - keyB);

    return sortedKeys.map(key => ({
      day: key,
      month: key,
      currentCount: Number(currentByKey.get(key)?.count) || 0,
      previousCount: Number(previousByKey.get(key)?.count) || 0,
      currentOrigins: currentByKey.get(key)?.origins || [],
      previousOrigins: previousByKey.get(key)?.origins || [],
    }));
  }, [data, previousData, isYearlyView]);

  useEffect(() => {
    setSelectedPoint(null);
  }, [currentLabel, previousLabel, isYearlyView]);

  const handleChartClick = (chartState) => {
    if (isYearlyView || !chartState?.activePayload?.length) return;
    setSelectedPoint(chartState.activePayload[0].payload);
  };

  const handleChartActivePointChange = (chartState) => {
    setActivePoint(chartState?.activePayload?.[0]?.payload || null);
  };

  const handleChartKeyDown = (keyboardEvent) => {
    if (isYearlyView || !activePoint || !OPEN_BREAKDOWN_KEYS.includes(keyboardEvent.key)) return;
    keyboardEvent.preventDefault();
    setSelectedPoint(activePoint);
  };

  const handleCloseBreakdown = () => {
    setSelectedPoint(null);
  };

  if (!mergedData || mergedData.length === 0) {
    return (
      <div className="d-flex justify-content-center text-muted p-4">
        {NO_DATA_MESSAGE}
      </div>
    );
  }

  const xAxisDataKey = isYearlyView ? 'month' : 'day';

  const getWeekdayLetter = (dayNumber) => {
    const weekday = getJsWeekday(dayNumber, year, month);
    if (weekday === null) {
      return '';
    }
    return WEEKDAY_LETTERS_BY_JS_DAY[weekday];
  };

  const renderDayTickWithWeekday = (tickProps) => {
    const horizontalPosition = tickProps.x;
    const verticalPosition = tickProps.y;
    const dayValue = tickProps.payload.value;
    const weekdayLetter = getWeekdayLetter(dayValue);
    return (
      <g transform={`translate(${horizontalPosition},${verticalPosition})`}>
        <text x={0} y={0} dy={DAY_TICK_VALUE_DY} textAnchor="middle" fill="#666" fontSize={12}>
          {dayValue}
        </text>
        <text x={0} y={0} dy={WEEKDAY_TICK_VALUE_DY} textAnchor="middle" fill="#999" fontSize={11}>
          {weekdayLetter}
        </text>
      </g>
    );
  };

  return (
    <>
      <div onKeyDown={handleChartKeyDown} onMouseDown={preventFocusOnMouseDown}>
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <LineChart
            data={mergedData}
            margin={CHART_MARGINS}
            className={isYearlyView ? '' : 'clickableChart'}
            accessibilityLayer
            onClick={handleChartClick}
            onMouseMove={handleChartActivePointChange}
          >
            <CartesianGrid strokeDasharray={GRID_DASH_PATTERN} />
            <XAxis
              dataKey={xAxisDataKey}
              tickFormatter={isYearlyView ? getMonthLabel : undefined}
              tick={isYearlyView ? undefined : renderDayTickWithWeekday}
              height={isYearlyView ? undefined : X_AXIS_HEIGHT}
              interval={isYearlyView ? undefined : 0}
            />
            <YAxis
              label={{ value: Y_AXIS_LABEL, angle: -90, position: 'insideLeft' }}
            />
            <Tooltip
              content={(
                <DailySalesTooltip
                  isYearlyView={isYearlyView}
                  currentLabel={currentLabel}
                  previousLabel={previousLabel}
                  showPreviousLine={showPreviousLine}
                  year={year}
                  month={month}
                  previousYear={previousYear}
                  previousMonth={previousMonth}
                />
              )}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="currentCount"
              name={currentLabel}
              stroke={COLORS.primaryStroke}
              strokeWidth={LINE_THICKNESS_PX}
              dot={CURRENT_PERIOD_POINT}
              activeDot={CURRENT_PERIOD_HOVERED_POINT}
            />
            {showPreviousLine && (
              <Line
                type="monotone"
                dataKey="previousCount"
                name={previousLabel}
                stroke={COLORS.secondary}
                strokeWidth={LINE_THICKNESS_PX}
                strokeDasharray={PREVIOUS_PERIOD_LINE_DASH_PATTERN}
                dot={PREVIOUS_PERIOD_POINT}
                activeDot={PREVIOUS_PERIOD_HOVERED_POINT}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {selectedPoint && (
        <SalesOriginBreakdown
          point={selectedPoint}
          currentLabel={currentLabel}
          previousLabel={previousLabel}
          showPreviousLine={showPreviousLine}
          onClose={handleCloseBreakdown}
        />
      )}
    </>
  );
};

export default DailySalesTimeSeriesChart;
